import { useState, useEffect, useRef } from 'react';
import { Link as RouterLink, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import './LoginPage.css';

/* ============================================================================
 * Validation schemas — preserved exactly from the previous implementation so
 * server-side expectations remain consistent.
 * ==========================================================================*/

const signInSchema = z.object({
  email: z.string().email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

const signUpSchema = signInSchema.extend({
  fullName: z.string().min(1, 'Name is required').max(120),
  password: z.string().min(6, 'Use at least 6 characters'),
});

function formatAuthError(err, fallback) {
  const msg = err?.message ?? String(err ?? '');
  if (/postMessage/i.test(msg) && /clone|could not be cloned/i.test(msg)) {
    return 'This request was blocked by a browser extension or page script. Try a private window, disable extensions for localhost, or use another browser.';
  }
  if (/failed to fetch/i.test(msg) || msg === 'NetworkError when attempting to fetch resource.') {
    return 'Cannot reach Supabase. In .env, set REACT_APP_SUPABASE_URL to the Project URL from Supabase Dashboard (Settings → API) — it must match the same project as your anon key. Then restart the dev server.';
  }
  return msg || fallback;
}

/* ============================================================================
 * Inline SVG icons (dependency-free, matches reference stroke weights)
 * ==========================================================================*/

const Envelope = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="20" height="16" x="2" y="4" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const Lock = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="18" height="11" x="3" y="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const EyeOpen = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOff = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
    <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
    <line x1="2" x2="22" y1="2" y2="22" />
  </svg>
);

const LinkGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const GoogleMark = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
    <path d="M16.51 8.18c0-.55-.05-1.07-.14-1.57H9v3.07h4.21c-.18 1-.74 1.85-1.57 2.41v2h2.54c1.49-1.37 2.34-3.39 2.34-5.91z" fill="#4285F4" />
    <path d="M9 17c2.12 0 3.9-.7 5.2-1.91l-2.54-2c-.7.47-1.6.75-2.66.75-2.05 0-3.78-1.38-4.4-3.24H1.97v2.07A8 8 0 0 0 9 17z" fill="#34A853" />
    <path d="M4.6 10.6a4.8 4.8 0 0 1 0-3.07V5.46H1.97a8 8 0 0 0 0 7.18l2.63-2.04z" fill="#FBBC05" />
    <path d="M9 4.04c1.16 0 2.2.4 3.02 1.18l2.25-2.25C12.9 1.74 11.12 1 9 1A8 8 0 0 0 1.97 5.46l2.63 2.07C5.22 5.42 6.95 4.04 9 4.04z" fill="#EA4335" />
  </svg>
);

const GitHubMark = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.4 3-.405 1.02.005 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
  </svg>
);

const Spinner = () => (
  <svg className="lfl-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden>
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

const LogoMark = () => (
  <span className="lfl-logo-mark" aria-hidden>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ff5436" strokeWidth="2.5" strokeLinecap="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  </span>
);

/* ============================================================================
 * Live-activity card — the right-side panel animates counters every ~2.8s
 * ==========================================================================*/

const INITIAL_ACTIVITY = [
  { slug: 'lfo.to/spring-26', location: 'Helmond, NL · just now', flag: 'lfl-nl', variant: '', count: 1 },
  { slug: 'lfo.to/webinar-may', location: 'San Francisco, US · 4s ago', flag: 'lfl-us', variant: 'lfl-olive', count: 12 },
  { slug: 'lfo.to/yt-promo', location: 'Berlin, DE · 11s ago', flag: 'lfl-de', variant: 'lfl-mustard', count: 3 },
  { slug: 'lfo.to/blog-pricing', location: 'Tokyo, JP · 23s ago', flag: 'lfl-jp', variant: '', count: 2 },
];

function ActivityStack() {
  const [items, setItems] = useState(INITIAL_ACTIVITY);
  const [bumpIndex, setBumpIndex] = useState(null);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return undefined;

    const timer = window.setInterval(() => {
      setItems((prev) => {
        const i = Math.floor(Math.random() * prev.length);
        const inc = Math.floor(Math.random() * 3) + 1;
        const next = prev.map((it, idx) => (idx === i ? { ...it, count: it.count + inc } : it));
        setBumpIndex(i);
        return next;
      });
      window.setTimeout(() => setBumpIndex(null), 300);
    }, 2800);

    return () => window.clearInterval(timer);
  }, []);

  return (
    <div className="lfl-activity-stack">
      <div className="lfl-activity-label">Live activity · last 60s</div>
      {items.map((item, i) => (
        <div className="lfl-activity-card" key={item.slug}>
          <div className={`lfl-activity-icon ${item.variant}`}>
            <LinkGlyph />
          </div>
          <div className="lfl-activity-content">
            <div className="lfl-activity-slug">{item.slug}</div>
            <div className="lfl-activity-meta">
              <span className={`lfl-activity-flag ${item.flag}`}>
                {item.flag === 'lfl-nl' && (<><div /><div /><div /></>)}
              </span>
              {item.location}
            </div>
          </div>
          <div className={`lfl-activity-count ${bumpIndex === i ? 'lfl-bump' : ''}`}>
            +{item.count}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ============================================================================
 * LoginPage — route "/login"
 * ==========================================================================*/

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';
  const {
    user,
    loading,
    signInWithEmail,
    signUpWithEmail,
    loginWithGoogle,
    resetPasswordForEmail,
  } = useAuth();

  const [mode, setMode] = useState('signin');
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSending, setForgotSending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const formTopRef = useRef(null);

  const signInForm = useForm({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: '', password: '' },
  });

  const signUpForm = useForm({
    resolver: zodResolver(signUpSchema),
    defaultValues: { email: '', password: '', fullName: '' },
  });

  if (!loading && user) {
    return <Navigate to={from} replace />;
  }

  if (loading) {
    return (
      <div className="lfl-root">
        <div className="lfl-loading">
          <div className="lfl-loading-pill">
            <Spinner />
          </div>
          <p style={{ fontSize: 13, color: '#6b6b72' }}>Loading…</p>
        </div>
      </div>
    );
  }

  const onSignIn = async (values) => {
    setSubmitting(true);
    try {
      await signInWithEmail(values.email, values.password);
      toast.success('Signed in');
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(formatAuthError(err, 'Could not sign in'));
    } finally {
      setSubmitting(false);
    }
  };

  const onSignUp = async (values) => {
    setSubmitting(true);
    try {
      const { needsEmailConfirmation } = await signUpWithEmail(
        values.email,
        values.password,
        values.fullName.trim(),
      );
      if (needsEmailConfirmation) {
        toast.success('Check your inbox to confirm your email, then sign in.');
        setMode('signin');
        signInForm.setValue('email', values.email);
      } else {
        toast.success('Account created');
        navigate(from, { replace: true });
      }
    } catch (err) {
      toast.error(formatAuthError(err, 'Could not create account'));
    } finally {
      setSubmitting(false);
    }
  };

  const onForgotSubmit = async (e) => {
    e?.preventDefault?.();
    if (!forgotEmail.trim()) {
      toast.error('Enter your email');
      return;
    }
    setForgotSending(true);
    try {
      await resetPasswordForEmail(forgotEmail.trim());
      toast.success('If an account exists, you will receive a reset link.');
      setForgotOpen(false);
      setForgotEmail('');
    } catch (err) {
      toast.error(formatAuthError(err, 'Could not send reset email'));
    } finally {
      setForgotSending(false);
    }
  };

  const onGoogle = async () => {
    try {
      await loginWithGoogle();
    } catch (err) {
      toast.error(formatAuthError(err, 'Google sign-in failed'));
    }
  };

  const onGithub = () => {
    toast.message('GitHub sign-in is coming soon', {
      description: 'For now, use Google or continue with email below.',
    });
  };

  const toggleMode = () => {
    const next = mode === 'signin' ? 'signup' : 'signin';
    setMode(next);
    setForgotOpen(false);
    window.setTimeout(() => {
      formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 40);
  };

  const isSignup = mode === 'signup';

  return (
    <div className="lfl-root">
      <div className="lfl-paper" aria-hidden />

      <div className="lfl-shell">
        {/* ============================  FORM SIDE  ============================ */}
        <div className="lfl-form-side">
          <div className="lfl-top-bar">
            <RouterLink to="/" className="lfl-logo">
              <LogoMark />
              LinkFlow
            </RouterLink>
            <div className="lfl-top-meta">
              {isSignup ? 'Already have an account?' : 'New here?'}
              <button type="button" onClick={toggleMode}>
                {isSignup ? 'Sign in' : 'Create an account'}
              </button>
            </div>
          </div>

          <div className="lfl-form-wrap" ref={formTopRef}>
            <div className="lfl-form-label">{isSignup ? 'Create account' : 'Welcome back'}</div>
            <h1 className="lfl-h1">
              {isSignup ? (
                <>Start <em>your</em> flow.</>
              ) : (
                <>Sign in to <em>your</em> flow.</>
              )}
            </h1>
            <p className="lfl-form-sub">
              {isSignup
                ? 'Free forever for your first 5,000 monthly clicks. No credit card required.'
                : 'Pick up where you left off. Your links, dashboards and analytics — exactly as you left them.'}
            </p>

            {/* SSO */}
            <div className="lfl-sso-row">
              <button type="button" className="lfl-sso-btn" onClick={onGoogle}>
                <GoogleMark />
                Google
              </button>
              <button type="button" className="lfl-sso-btn" onClick={onGithub}>
                <GitHubMark />
                GitHub
              </button>
            </div>

            <div className="lfl-divider">or continue with email</div>

            {isSignup ? (
              <form onSubmit={signUpForm.handleSubmit(onSignUp)} noValidate>
                <div className="lfl-field">
                  <label className="lfl-field-label" htmlFor="signup-name">Full name</label>
                  <div className="lfl-field-wrap">
                    <span className="lfl-field-icon"><UserIcon /></span>
                    <input
                      id="signup-name"
                      type="text"
                      autoComplete="name"
                      placeholder="Ganesh Kudikala"
                      className={`lfl-field-input ${signUpForm.formState.errors.fullName ? 'lfl-error' : ''}`}
                      {...signUpForm.register('fullName')}
                    />
                  </div>
                  {signUpForm.formState.errors.fullName && (
                    <p className="lfl-field-error">{signUpForm.formState.errors.fullName.message}</p>
                  )}
                </div>

                <div className="lfl-field">
                  <label className="lfl-field-label" htmlFor="signup-email">Work email</label>
                  <div className="lfl-field-wrap">
                    <span className="lfl-field-icon"><Envelope /></span>
                    <input
                      id="signup-email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@company.com"
                      className={`lfl-field-input ${signUpForm.formState.errors.email ? 'lfl-error' : ''}`}
                      {...signUpForm.register('email')}
                    />
                  </div>
                  {signUpForm.formState.errors.email && (
                    <p className="lfl-field-error">{signUpForm.formState.errors.email.message}</p>
                  )}
                </div>

                <div className="lfl-field">
                  <label className="lfl-field-label" htmlFor="signup-password">Password</label>
                  <div className="lfl-field-wrap">
                    <span className="lfl-field-icon"><Lock /></span>
                    <input
                      id="signup-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      placeholder="At least 6 characters"
                      className={`lfl-field-input ${signUpForm.formState.errors.password ? 'lfl-error' : ''}`}
                      {...signUpForm.register('password')}
                    />
                    <button
                      type="button"
                      className="lfl-field-toggle"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff /> : <EyeOpen />}
                    </button>
                  </div>
                  {signUpForm.formState.errors.password ? (
                    <p className="lfl-field-error">{signUpForm.formState.errors.password.message}</p>
                  ) : (
                    <p className="lfl-field-hint">Use at least 6 characters.</p>
                  )}
                </div>

                <button type="submit" className="lfl-submit-btn" disabled={submitting} data-testid="signup-btn">
                  {submitting ? (
                    <>
                      <Spinner /> Creating account…
                    </>
                  ) : (
                    <>
                      Create account <span className="lfl-arrow">→</span>
                    </>
                  )}
                </button>
              </form>
            ) : (
              <form onSubmit={signInForm.handleSubmit(onSignIn)} noValidate>
                <div className="lfl-field">
                  <label className="lfl-field-label" htmlFor="signin-email">Work email</label>
                  <div className="lfl-field-wrap">
                    <span className="lfl-field-icon"><Envelope /></span>
                    <input
                      id="signin-email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@company.com"
                      className={`lfl-field-input ${signInForm.formState.errors.email ? 'lfl-error' : ''}`}
                      {...signInForm.register('email')}
                    />
                  </div>
                  {signInForm.formState.errors.email && (
                    <p className="lfl-field-error">{signInForm.formState.errors.email.message}</p>
                  )}
                </div>

                <div className="lfl-field">
                  <label className="lfl-field-label" htmlFor="signin-password">Password</label>
                  <div className="lfl-field-wrap">
                    <span className="lfl-field-icon"><Lock /></span>
                    <input
                      id="signin-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="••••••••••••"
                      className={`lfl-field-input ${signInForm.formState.errors.password ? 'lfl-error' : ''}`}
                      {...signInForm.register('password')}
                    />
                    <button
                      type="button"
                      className="lfl-field-toggle"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff /> : <EyeOpen />}
                    </button>
                  </div>
                  {signInForm.formState.errors.password && (
                    <p className="lfl-field-error">{signInForm.formState.errors.password.message}</p>
                  )}
                </div>

                <div className="lfl-forgot-row">
                  <label className="lfl-checkbox-row">
                    <input
                      type="checkbox"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                    />
                    Keep me signed in
                  </label>
                  <button
                    type="button"
                    className="lfl-forgot-link"
                    onClick={() => {
                      const next = !forgotOpen;
                      setForgotOpen(next);
                      if (next) setForgotEmail(signInForm.getValues('email') || '');
                    }}
                  >
                    Forgot password?
                  </button>
                </div>

                {forgotOpen && (
                  <div className="lfl-forgot-panel">
                    <p className="lfl-forgot-panel-hint">
                      We will email you a link to reset your password.
                    </p>
                    <div className="lfl-field-wrap">
                      <span className="lfl-field-icon"><Envelope /></span>
                      <input
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), onForgotSubmit(e))}
                        placeholder="you@company.com"
                        className="lfl-field-input"
                      />
                    </div>
                    <div className="lfl-forgot-panel-actions">
                      <button
                        type="button"
                        className="lfl-primary"
                        disabled={forgotSending}
                        onClick={onForgotSubmit}
                      >
                        {forgotSending ? 'Sending…' : 'Send reset link'}
                      </button>
                      <button
                        type="button"
                        className="lfl-secondary"
                        onClick={() => setForgotOpen(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                <button type="submit" className="lfl-submit-btn" disabled={submitting} data-testid="signin-btn">
                  {submitting ? (
                    <>
                      <Spinner /> Signing in…
                    </>
                  ) : (
                    <>
                      Sign in <span className="lfl-arrow">→</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          <div className="lfl-form-foot">
            <div>© {new Date().getFullYear()} LinkFlow</div>
            <div>
              <a href="#privacy">Privacy</a>
              <a href="#terms">Terms</a>
              <a href="#status">Status</a>
            </div>
          </div>
        </div>

        {/* ============================  VISUAL SIDE  ============================ */}
        <div className="lfl-visual-side">
          <div className="lfl-grid-bg" />

          <div className="lfl-quote-block">
            <span className="lfl-quote-mark">&ldquo;</span>
            <p className="lfl-quote-text">
              We replaced Bitly and a homegrown tracking script in one afternoon. The AI slugs alone save us an hour a week — and the realtime conversion data is what we'd been <em>begging</em> engineering for.
            </p>
            <div className="lfl-quote-author">
              <div className="lfl-author-avatar">MR</div>
              <div className="lfl-author-info">
                <div className="lfl-author-name">Maya Reinders</div>
                <div className="lfl-author-role">Head of Growth · Northwave</div>
              </div>
            </div>
          </div>

          <ActivityStack />
        </div>
      </div>
    </div>
  );
}
